import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['hiddenSelect', 'select', 'selectArrow', 'selectContainer', 'options', 'option', 'checkBox', 'searchForm'];

  static classes = ['blankText', 'selected', 'unselected', 'disabled'];

  static values = { multiple: Boolean };

  connect() {
    if (this.isDisabled()) {
      this.disabledClasses.forEach((cl) => this.selectContainerTarget.classList.add(cl));
    }

    if (this.multipleValue) {
      this.setValuesToMultipleSelect(this.getCurrentMultipleValues());
    } else {
      const defaultTarget = this.getDefaultTarget();
      if (defaultTarget) {
        defaultTarget.click();
      }
    }
  }

  getDefaultTarget() {
    return this.optionTargets.find((el) => el.dataset.value === this.hiddenSelectTarget.value);
  }

  incrementalSearch() {
    const searchWord = this.searchFormTarget.value;

    this.optionTargets.forEach((target) => {
      if (target.dataset.key.indexOf(searchWord) >= 0) {
        target.classList.remove('hidden');
      } else {
        target.classList.add('hidden');
      }
    });
  }

  isDisabled() {
    return this.hiddenSelectTarget.disabled;
  }

  toggleOptions() {
    if (this.isDisabled()) return;

    if (this.optionsTarget.classList.contains('hidden')) {
      this.showOptions();
    } else {
      this.hideOptions();
    }
  }

  showOptions() {
    this.optionsTarget.classList.remove('hidden');
    this.selectArrowTarget.classList.add('rotate-180');
    const selectedOption = this.optionTargets.find(
      (el) => el.classList.contains(this.selectedClass),
    );
    if (this.hasSearchFormTarget) {
      this.searchFormTarget.value = '';
      this.incrementalSearch();
    }
    if (selectedOption) {
      selectedOption.focus();
    }
  }

  hideOptions() {
    this.optionsTarget.classList.add('hidden');
    this.selectArrowTarget.classList.remove('rotate-180');
  }

  globalClickHandler(e) {
    if (!this.element.contains(e.target)) {
      this.hideOptions();
    }
  }

  getCurrentMultipleValues() {
    return Array.from(this.hiddenSelectTarget.selectedOptions).map((option) => option.value);
  }

  updateMultipleValue(e) {
    const value = String(e.params.value);
    const currentValues = this.getCurrentMultipleValues();
    let newValues;

    if (currentValues.includes(value)) {
      newValues = currentValues.filter((currentValue) => currentValue !== value);
    } else {
      newValues = currentValues.concat(value);
    }

    this.setValuesToMultipleSelect(newValues);
  }

  setValuesToMultipleSelect(newValues) {
    const keys = [];
    Array.from(this.hiddenSelectTarget.options).forEach((option) => {
      if (newValues.includes(option.value)) {
        option.selected = true;
        keys.push(option.innerHTML);
      } else {
        option.selected = false;
      }
    });

    this.checkBoxTargets.forEach((checkBox) => {
      checkBox.checked = newValues.includes(checkBox.value);
    });

    this.optionTargets.forEach((optionTarget) => {
      if (newValues.includes(optionTarget.dataset.value)) {
        this.addSelectedClassToOptionTarget(optionTarget);
      } else {
        this.addUnselectedClassToOptionTarget(optionTarget);
      }
    });

    const displayText = keys.join(', ');
    this.selectTarget.innerHTML = displayText || '選択してください';
    if (displayText) {
      this.selectContainerTarget.classList.remove(this.blankTextClass);
    } else {
      this.selectContainerTarget.classList.add(this.blankTextClass);
    }
  }

  addSelectedClassToOptionTarget(optionTarget) {
    this.selectedClasses.forEach((cl) => optionTarget.classList.add(cl));
    this.unselectedClasses.forEach((cl) => optionTarget.classList.remove(cl));
  }

  addUnselectedClassToOptionTarget(optionTarget) {
    this.unselectedClasses.forEach((cl) => optionTarget.classList.add(cl));
    this.selectedClasses.forEach((cl) => optionTarget.classList.remove(cl));
  }

  updateValue(e) {
    if (this.hiddenSelectTarget.hasAttribute('multiple')) {
      this.updateMultipleValue(e);
      return;
    }

    const { key } = e.params;
    const { value } = e.params;
    this.selectTarget.innerHTML = key;
    if (value) {
      this.selectContainerTarget.classList.remove(this.blankTextClass);
    } else {
      this.selectContainerTarget.classList.add(this.blankTextClass);
    }

    this.optionTargets.forEach((optionTarget) => {
      this.addUnselectedClassToOptionTarget(optionTarget);
    });
    this.addSelectedClassToOptionTarget(e.currentTarget);
    this.hiddenSelectTarget.value = value;
    this.hiddenSelectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.hideOptions();
  }

  updateSelectedOption() {
    const selectedOption = this.optionTargets.find(
      (el) => el.dataset.value === this.hiddenSelectTarget.value,
    );

    if (selectedOption && !selectedOption.classList.contains(this.selectedClass)) {
      selectedOption.click();
    }
  }

  resetSelection() {
    // hiddenSelectの値を空にする
    this.hiddenSelectTarget.value = '';

    // 表示テキストを「選択してください」にする
    this.selectTarget.innerHTML = '選択してください';
    this.selectContainerTarget.classList.add(this.blankTextClass);

    // 全てのオプションの選択状態をリセット
    this.optionTargets.forEach((optionTarget) => {
      this.addUnselectedClassToOptionTarget(optionTarget);
    });

    // changeイベントを発火
    this.hiddenSelectTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
